import QuestionsI18nJSON from './questions.i18n.json';
import Questions from './questions';
import type {AppLanguageCode} from '../Utils/I18n';

// Per-language overlay of clinical question/answer text. Titles are keyed by
// the question `refId`; answer labels are keyed by their *normalized English
// label* (not the choice index) so the overlay survives model re-exports that
// renumber choice keys, and so the 53 unique labels are translated once and
// reused across every question that shares them.
type LanguageOverlay = {
  titles?: Record<string, string>;
  answer_options?: Record<string, string>;
};

const OVERLAYS = QuestionsI18nJSON as Partial<Record<AppLanguageCode, LanguageOverlay>>;

export type QuestionId = keyof typeof Questions;

export type LocalizedQuestion = {
  title: string;
  description?: string;
  choices: Record<string, string>;
  sort?: string[];
};

// Must mirror the normalization used by import_reviewed_translations.py when it
// builds the `answer_options` keys: NFKC, curly→straight apostrophes, collapsed
// whitespace. Keeps "Don't know" / "Don’t know" lookups in sync.
export const normalizeLabel = (label: string): string =>
  label
    .normalize('NFKC')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Return the question title + answer choices in the requested language, falling
 * back per-field to the canonical English text from `questions.json` whenever a
 * translation is missing. With an empty overlay this is identical to English,
 * so unreviewed languages display English until translations are imported.
 */
export const getLocalizedQuestion = (
  id: QuestionId,
  language: AppLanguageCode = 'en',
): LocalizedQuestion => {
  const base = Questions[id];
  if (language === 'en') {
    return {
      title: base.title,
      description: base.description,
      choices: base.choices,
      sort: base.sort,
    };
  }

  const overlay = OVERLAYS[language];
  const titleOverride = overlay?.titles?.[id as string];
  const optionMap = overlay?.answer_options ?? {};

  const choices: Record<string, string> = {};
  for (const [key, englishLabel] of Object.entries(base.choices)) {
    choices[key] = optionMap[normalizeLabel(englishLabel)] ?? englishLabel;
  }

  return {
    title: titleOverride ?? base.title,
    description: base.description,
    choices,
    sort: base.sort,
  };
};
